/*
 * Copyright (c) 2025. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import type { LatLng } from 'leaflet';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMap } from 'react-leaflet';
import { useDebouncedCallback } from '../useDebouncedCallback';

export interface HoverControllerState<TItem, TSource extends string = string> {
  hoveredItem: TItem | null;
  hoverAnchor: LatLng | null;
  hoverSource: TSource | null;
  pinnedKey: string | null;
  isHoveringCard: boolean;
}

export interface HoverControllerHandlers<TItem, TSource extends string = string> {
  onHoverStart: (item: TItem, anchor: LatLng, source: TSource) => void;
  onHoverEnd: () => void;
  onClick: (item: TItem, anchor: LatLng, source: TSource) => void;
  onCardMouseEnter: () => void;
  onCardMouseLeave: () => void;
  clear: () => void;
}

interface UseHoverControllerOptions<TItem> {
  getItemKey: (item: TItem) => string;
  hoverDelay?: number;
  hoverEndDelay?: number;
}

export default function useParcelHoverController<TItem, TSource extends string = string>({
  getItemKey,
  hoverDelay = 150,
  hoverEndDelay = 60,
}: UseHoverControllerOptions<TItem>) {
  const map = useMap();
  const [hoveredItem, setHoveredItem] = useState<TItem | null>(null);
  const [hoverAnchor, setHoverAnchor] = useState<LatLng | null>(null);
  const [hoverSource, setHoverSource] = useState<TSource | null>(null);
  const [isHoveringCard, setIsHoveringCard] = useState(false);
  const [pinnedKey, setPinnedKey] = useState<string | null>(null);

  const clearHoverOnlyImmediate = useCallback(() => {
    setHoveredItem(null);
    setHoverAnchor(null);
    setHoverSource(null);
  }, []);

  const onHoverStart = useDebouncedCallback((item: TItem, anchor: LatLng, source: TSource) => {
    if (pinnedKey) return;
    setHoveredItem(item);
    setHoverAnchor(anchor);
    setHoverSource(source);
  }, hoverDelay);

  const clearHoverOnly = useDebouncedCallback(() => {
    if (pinnedKey) return;
    if (isHoveringCard) return;
    clearHoverOnlyImmediate();
  }, hoverEndDelay);

  const clear = useCallback(() => {
    onHoverStart.cancel();
    clearHoverOnly.cancel();
    setHoveredItem(null);
    setHoverAnchor(null);
    setHoverSource(null);
    setPinnedKey(null);
    setIsHoveringCard(false);
  }, [clearHoverOnly, onHoverStart]);

  const onHoverEnd = useCallback(() => {
    onHoverStart.cancel();
    clearHoverOnly();
  }, [clearHoverOnly, onHoverStart]);

  const onClick = useCallback(
    (item: TItem, anchor: LatLng, source: TSource) => {
      onHoverStart.cancel();
      clearHoverOnly.cancel();
      const key = getItemKey(item);
      if (pinnedKey && pinnedKey === key) {
        setPinnedKey(null);
        setIsHoveringCard(false);
        clearHoverOnlyImmediate();
        return;
      }
      setPinnedKey(key);
      setHoveredItem(item);
      setHoverAnchor(anchor);
      setHoverSource(source);
    },
    [clearHoverOnly, clearHoverOnlyImmediate, getItemKey, onHoverStart, pinnedKey],
  );

  const onCardMouseEnter = useCallback(() => {
    clearHoverOnly.cancel();
    setIsHoveringCard(true);
  }, [clearHoverOnly]);
  const onCardMouseLeave = useCallback(() => {
    setIsHoveringCard(false);
    if (!pinnedKey) clearHoverOnlyImmediate();
  }, [clearHoverOnlyImmediate, pinnedKey]);

  // Escape clears
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' && e.key !== 'Esc') return;
      clear();
    };
    // Use capture so we still see Escape even if a focused control stops propagation.
    window.addEventListener('keydown', onKeyDown, { capture: true });
    document.addEventListener('keydown', onKeyDown, { capture: true });
    return () => {
      window.removeEventListener('keydown', onKeyDown, {
        capture: true,
      } as AddEventListenerOptions);
      document.removeEventListener('keydown', onKeyDown, {
        capture: true,
      } as AddEventListenerOptions);
    };
  }, [clear]);

  // Map move/zoom clears
  useEffect(() => {
    if (!map) return;
    const onMoveStart = () => clear();
    map.on('movestart', onMoveStart);
    map.on('zoomstart', onMoveStart);
    return () => {
      map.off('movestart', onMoveStart);
      map.off('zoomstart', onMoveStart);
    };
  }, [map, clear]);

  const state = useMemo<HoverControllerState<TItem, TSource>>(
    () => ({ hoveredItem, hoverAnchor, hoverSource, pinnedKey, isHoveringCard }),
    [hoveredItem, hoverAnchor, hoverSource, pinnedKey, isHoveringCard],
  );

  const handlers = useMemo<HoverControllerHandlers<TItem, TSource>>(
    () => ({
      onHoverStart,
      onHoverEnd,
      onClick,
      onCardMouseEnter,
      onCardMouseLeave,
      clear,
    }),
    [onHoverStart, onHoverEnd, onClick, onCardMouseEnter, onCardMouseLeave, clear],
  );

  return { state, handlers };
}
